import React, { useState } from 'react';
import './Box.css';
import DemoLoginModule from '../modules/DemoLoginModule';
import DemoReportsListModule from '../modules/DemoReportsListModule';
import DemoReportEditModule from '../modules/DemoReportEditModule';
import DemoReportFeedbackModule from '../modules/DemoReportFeedbackModule';
import DemoClinicalFeedbackModule from '../modules/DemoClinicalFeedbackModule';

const DemoTestBox = ({ isSmallScreen }) => {
    const [currentState, setCurrentState] = useState('login');
    const [selectedReportXml, setSelectedReportXml] = useState(null);
    const [editedReportXml, setEditedReportXml] = useState(null);
    const [reportId, setReportId] = useState(null);
    const [selectedReportName, setSelectedReportName] = useState(null);
    const [sessionAgency, setSessionAgency] = useState(null);
    const [codeData, setCodeData] = useState(null);
    const [hasSubmittedReport, setHasSubmittedReport] = useState(false);

    const handleLoginSuccess = (agency, data) => {
        setSessionAgency(agency);
        setCodeData(data);
        setCurrentState('reportSelection');
    };

    const handleReportSelect = (reportXml, reportName) => {
        setSelectedReportXml(reportXml);
        setEditedReportXml(null);
        setSelectedReportName(reportName);
        setHasSubmittedReport(false);
        setReportId(null);
        setCurrentState('reportEdit');
    };

    const handleReportFeedback = (reportId, reportName) => {
        setReportId(reportId);
        setSelectedReportName(reportName);
        setSelectedReportXml(null);
        setCurrentState('reportFeedback');
    };

    const handleBack = () => {
        setCurrentState('reportSelection');
    };

    const handleAccessFeedback = (newReportId) => {
        setReportId(newReportId);
        setCurrentState('reportFeedback');
    };

    const handleAccessClinicalFeedback = (newReportId) => {
        setReportId(newReportId);
        setCurrentState('clinicalFeedback');
    };

    const handleRestartDemo = () => {
        setCurrentState('reportSelection');
        setSelectedReportXml(null);
        setEditedReportXml(null);
        setReportId(null);
        setSelectedReportName(null);
        setHasSubmittedReport(false);
    };

    const handleUsageUpdate = (updatedCodeData) => {
        setCodeData(updatedCodeData);
    };

    const handleReportSubmitted = (submittedReportId) => {
        setReportId(submittedReportId);
        setHasSubmittedReport(true);
    };

    // Helper function to get the XML to pass to DemoReportEditModule
    const getCurrentXml = () => {
        return editedReportXml || selectedReportXml;
    };

    // Helper function to update edited XML (to be called from DemoReportEditModule)
    const handleXmlUpdate = (updatedXml) => {
        setEditedReportXml(updatedXml);
    };

    // Helper function to get current progress step
    const getCurrentStep = () => {
        switch (currentState) {
            case 'login':
            case 'reportSelection':
                return 1;
            case 'reportEdit':
                return 2;
            case 'reportFeedback':
                return selectedReportXml ? 3 : 3;
            case 'clinicalFeedback':
                return 4;
            default:
                return 1;
        }
    };

    // Helper function to check if we can navigate back
    const canNavigateBack = () => {
        const currentStep = getCurrentStep();
        if (currentStep <= 1) return false;
        
        if (currentState === 'reportFeedback' && !selectedReportXml) return false;
        
        return true;
    };

    // Helper function to check if we can navigate forward
    const canNavigateForward = () => {
        const currentStep = getCurrentStep();
        if (currentStep >= 4) return false;
        
        if (currentStep === 1 && !selectedReportXml) return false;
        if (currentStep === 2 && !reportId) return false;
        if (currentStep === 3 && !reportId) return false;
        
        return true;
    };

    // Helper function to get current report display text
    const getCurrentReportText = () => {
        if (selectedReportName) {
            return `Selected: ${selectedReportName}`;
        }
        return 'No Report Selected';
    };

    const getCurrentSessionText = () => {
        if (sessionAgency) {
            return `Session: ${sessionAgency}`;
        }
        return 'No Session Selected';
    };

    // Navigation handlers
    const handlePreviousStep = () => {
        const currentStep = getCurrentStep();
        if (currentStep > 1) {
            if (currentStep === 4) {
                setCurrentState('reportFeedback');
            } else if (currentStep === 3) {
                setCurrentState('reportEdit');
            } else if (currentStep === 2) {
                setCurrentState('reportSelection');
            }
        }
    };

    const handleNextStep = () => {
        const currentStep = getCurrentStep();
        if (currentStep < 4) {
            if (currentStep === 1 && selectedReportXml) {
                setCurrentState('reportEdit');
            } else if (currentStep === 2 && reportId) {
                setCurrentState('reportFeedback');
            } else if (currentStep === 3 && reportId) {
                setCurrentState('clinicalFeedback');
            }
        }
    };

    // Progress indicator component
    const ProgressIndicator = () => {
        const currentStep = getCurrentStep();
        const steps = [
            { label: 'Select', step: 1 },
            { label: 'Submit', step: 2 },
            { label: 'Documentation', step: 3 },
            { label: 'Clinical', step: 4 }
        ];

        return (
            <div style={{ 
                marginBottom: '20px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                width: '100%'
            }}>
                {/* Left Text */}
                <div style={{ flex: 1, textAlign: 'left' }}>
                    <span style={{ fontSize: '14px', color: '#666', paddingLeft: '64px' }}>
                        Submissions: {codeData ? (codeData.number_uses || 0) : 0} used of 8
                    </span>
                </div>

                {/* Centered Progress Indicator */}
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '20px'
                }}>
                    {/* Left Arrow */}
                    <button
                        onClick={handlePreviousStep}
                        disabled={!canNavigateBack()}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '18px',
                            cursor: canNavigateBack() ? 'pointer' : 'not-allowed',
                            color: canNavigateBack() ? 'var(--blue)' : '#ccc',
                            padding: '8px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '40px',
                            minHeight: '40px'
                        }}
                    >
                        <i className="fas fa-chevron-left"></i>
                    </button>

                    {/* Progress Dots */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '300px' }}>
                        {steps.map((step, index) => {
                            let isActive = currentStep >= step.step;
                            let isCurrent = currentStep === step.step;
                            
                            if (currentState === 'reportFeedback' && !selectedReportXml) {
                                isActive = step.step === 3;
                                isCurrent = step.step === 3;
                            }
                            
                            return (
                                <div key={step.step} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flex: '1' }}>
                                    <div
                                        style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '50%',
                                            backgroundColor: isActive ? 'var(--blue)' : '#e0e0e0',
                                            border: isCurrent ? '2px solid var(--blue)' : '2px solid #e0e0e0',
                                            transition: 'all 0.3s ease'
                                        }}
                                    />
                                    <span style={{
                                        fontSize: '12px',
                                        color: isActive ? 'var(--blue)' : '#666',
                                        fontWeight: isCurrent ? 'bold' : 'normal',
                                        textAlign: 'center'
                                    }}>
                                        {step.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Right Arrow */}
                    <button
                        onClick={handleNextStep}
                        disabled={!canNavigateForward()}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '18px',
                            cursor: canNavigateForward() ? 'pointer' : 'not-allowed',
                            color: canNavigateForward() ? 'var(--blue)' : '#ccc',
                            padding: '8px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '40px',
                            minHeight: '40px'
                        }}
                    >
                        <i className="fas fa-chevron-right"></i>
                    </button>
                </div>

                {/* Right Text - Report Information */}
                <div style={{ flex: 1, textAlign: 'right' }}>
                    <span style={{ fontSize: '14px', color: '#666', paddingRight: '64px' }}>
                        {getCurrentReportText()}
                    </span>
                </div>
            </div>
        );
    };

    const renderCurrentState = () => {
        switch (currentState) {
            case 'login':
                return <DemoLoginModule onLoginSuccess={handleLoginSuccess} />;
            case 'reportSelection':
                return (
                    <div>
                        <ProgressIndicator />
                        <DemoReportsListModule 
                            isSmallScreen={isSmallScreen} 
                            onReportSelect={handleReportSelect}
                            onReportFeedback={handleReportFeedback}
                            sessionAgency={sessionAgency}
                        />
                    </div>
                );
            case 'reportEdit':
                return (
                    <div>
                        <ProgressIndicator />
                        <DemoReportEditModule 
                            reportXml={getCurrentXml()} 
                            onBack={handleBack}
                            onAccessFeedback={handleAccessFeedback}
                            onXmlUpdate={handleXmlUpdate}
                            sessionAgency={sessionAgency}
                            onUsageUpdate={handleUsageUpdate}
                            onReportSubmitted={handleReportSubmitted}
                            hasSubmittedReport={hasSubmittedReport}
                            reportId={reportId}
                            codeData={codeData}
                        />
                    </div>
                );
            case 'reportFeedback':
                return (
                    <div>
                        <ProgressIndicator />
                        <div style={{ 
                            marginBottom: '20px', 
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <button 
                                className="standard-button"
                                onClick={() => handleAccessClinicalFeedback(reportId)}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: 'var(--red)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    fontWeight: 'bold'
                                }}
                            >
                                View Clinical Analysis
                            </button>
                        </div>
                        <DemoReportFeedbackModule 
                            reportId={reportId}
                            isSmallScreen={isSmallScreen}
                        />
                    </div>
                );
            case 'clinicalFeedback':
                return (
                    <div>
                        <ProgressIndicator />
                        <div style={{ 
                            marginBottom: '20px', 
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <button 
                                className="standard-button"
                                onClick={handleRestartDemo}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: 'var(--blue)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    fontWeight: 'bold'
                                }}
                            >
                                Begin with Another Report
                            </button>
                        </div>
                        <DemoClinicalFeedbackModule 
                            reportId={reportId}
                            isSmallScreen={isSmallScreen}
                            selectedReportName={selectedReportName}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className={isSmallScreen ? "standard-box-screen-small" : "standard-box"}>
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: '12px'
            }}>
                <img 
                    src={require('../../../images/logo-dark-QA.png')} 
                    alt="CADMUS Logo" 
                    style={{
                        height: '50px',
                        marginRight: '10px'
                    }}
                />
                <h1 className="box-title" style={{ width: 'fit-content' }}>Demo</h1>
            </div>
            {renderCurrentState()}
        </div>
    );
};

export default DemoTestBox; 